import { useState, useEffect, useCallback } from 'react';

const ANALYSIS_MODES = {
  SINGLE_PERIOD: 'single_period',
  MONTHLY_4WEEKS: 'monthly_4weeks'
};

// Функция для получения текущей даты по московскому времени (UTC+3)
const getMoscowDate = () => {
  const now = new Date();
  const moscowDate = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Moscow' }));
  // Устанавливаем время на начало дня (00:00:00) в московском времени, но Date объект будет в локальном UTC
  // Для корректной работы с датами без времени, важно единообразно их обрабатывать как "даты в определенной таймзоне"
  // или всегда работать с UTC датами, если это возможно и логично для задачи.
  // Здесь мы ориентируемся на московский день.
  moscowDate.setHours(0, 0, 0, 0); 
  return moscowDate;
};

// Функция для определения ближайшей прошедшей даты из списка (1, 8, 15, 22)
const getNearestPastDay = (date) => {
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();
  const referenceDays = [1, 8, 15, 22];

  if (referenceDays.includes(day)) {
    return new Date(year, month, day); 
  }

  const pastReferenceDays = referenceDays.filter(refDay => refDay < day);
  if (pastReferenceDays.length > 0) {
    const nearestDay = Math.max(...pastReferenceDays);
    return new Date(year, month, nearestDay);
  } else {
    let prevMonthDate = new Date(year, month, 0); // Последний день предыдущего месяца
    const prevMonth = prevMonthDate.getMonth();
    const prevYear = prevMonthDate.getFullYear();
    // Находим ближайший референсный день в предыдущем месяце, не превышающий число дней в том месяце
    const validReferenceDaysPrevMonth = referenceDays.filter(refDay => refDay <= prevMonthDate.getDate());
    const nearestDayPrevMonth = Math.max(...validReferenceDaysPrevMonth, 1); // Берем 1 число, если ничего не подошло
    return new Date(prevYear, prevMonth, nearestDayPrevMonth);
  }
};

// Функция для форматирования даты в ISO строку (YYYY-MM-DD)
const formatDateToISO = (date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMonthToISO = (date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
};

const getMonthRangeFromValue = (monthValue) => {
  const match = typeof monthValue === 'string' ? monthValue.match(/^(\d{4})-(\d{2})$/) : null;

  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);

  if (month < 1 || month > 12) {
    return null;
  }

  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);

  return {
    startDate: formatDateToISO(monthStart),
    endDate: formatDateToISO(monthEnd)
  };
};

const getLatestAvailableMonthlyMonth = (date) => {
  if (date.getDate() >= 29) {
    return formatMonthToISO(date);
  }

  return formatMonthToISO(new Date(date.getFullYear(), date.getMonth() - 1, 1));
};

const normalizeMonthValue = (monthValue) => {
  return /^\d{4}-\d{2}$/.test(monthValue) ? monthValue : null;
};

export const useAnalysisParameters = () => {
  const [foundersFile, setFoundersFile] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [singlePeriodStartDate, setSinglePeriodStartDate] = useState('');
  const [singlePeriodEndDate, setSinglePeriodEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [postThreshold, setPostThreshold] = useState(3);
  const [analysisMode, setAnalysisMode] = useState(ANALYSIS_MODES.SINGLE_PERIOD);
  const [analysisError, setAnalysisError] = useState(null); // Оставим здесь, т.к. связано с проверкой параметров

  useEffect(() => {
    const moscowToday = getMoscowDate();
    const pastDate = getNearestPastDay(new Date(moscowToday)); // Передаем копию, чтобы не мутировать moscowToday
    const initialStartDate = formatDateToISO(pastDate);
    const initialEndDate = formatDateToISO(moscowToday);

    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    setSinglePeriodStartDate(initialStartDate);
    setSinglePeriodEndDate(initialEndDate);
    setSelectedMonth(getLatestAvailableMonthlyMonth(moscowToday));
  }, []);

  const handleFoundersUpload = useCallback((file) => {
    setFoundersFile(file);
    setAnalysisError(null); // Сбрасываем ошибку при загрузке нового файла
  }, []);

  const handleStartDateChange = useCallback((date) => {
    if (date instanceof Date && !isNaN(date)) {
      const nextStartDate = formatDateToISO(date);
      setStartDate(nextStartDate);
      setSinglePeriodStartDate(nextStartDate);
    } else {
      console.warn('Invalid date received in handleStartDateChange:', date);
    }
  }, []);

  const handleEndDateChange = useCallback((date) => {
    if (date instanceof Date && !isNaN(date)) {
      const nextEndDate = formatDateToISO(date);
      setEndDate(nextEndDate);
      setSinglePeriodEndDate(nextEndDate);
    } else {
      console.warn('Invalid date received in handleEndDateChange:', date);
    }
  }, []);
  
  const handlePostThresholdChange = useCallback((value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1) {
        setPostThreshold(numValue);
    } else {
        setPostThreshold(3); // Устанавливаем 3 при некорректном вводе
    }
  }, []);

  const handleAnalysisModeChange = useCallback((value) => {
    const nextMode = value === ANALYSIS_MODES.MONTHLY_4WEEKS
      ? ANALYSIS_MODES.MONTHLY_4WEEKS
      : ANALYSIS_MODES.SINGLE_PERIOD;

    if (nextMode === ANALYSIS_MODES.MONTHLY_4WEEKS) {
      const nextSelectedMonth = normalizeMonthValue(selectedMonth) || getLatestAvailableMonthlyMonth(getMoscowDate());
      const monthRange = getMonthRangeFromValue(nextSelectedMonth);

      if (nextSelectedMonth && monthRange) {
        setSelectedMonth(nextSelectedMonth);
        setStartDate(monthRange.startDate);
        setEndDate(monthRange.endDate);
      }
    } else {
      if (singlePeriodStartDate) {
        setStartDate(singlePeriodStartDate);
      }

      if (singlePeriodEndDate) {
        setEndDate(singlePeriodEndDate);
      }
    }

    setAnalysisMode(nextMode);
    setAnalysisError(null);
  }, [selectedMonth, singlePeriodEndDate, singlePeriodStartDate]);

  const handleSelectedMonthChange = useCallback((value) => {
    const nextSelectedMonth = normalizeMonthValue(value);
    const monthRange = getMonthRangeFromValue(nextSelectedMonth);

    if (!nextSelectedMonth || !monthRange) {
      return;
    }

    setSelectedMonth(nextSelectedMonth);
    setStartDate(monthRange.startDate);
    setEndDate(monthRange.endDate);
    setAnalysisError(null);
  }, []);

  return {
    foundersFile,
    startDate,
    endDate,
    selectedMonth,
    postThreshold,
    analysisMode,
    analysisError, // Возвращаем analysisError и его сеттер
    setAnalysisError,
    handleFoundersUpload,
    handleStartDateChange,
    handleEndDateChange,
    handleSelectedMonthChange,
    handlePostThresholdChange, // Возвращаем новый обработчик
    handleAnalysisModeChange,
    setFoundersFile, // Возвращаем сеттеры, если они нужны вовне
    setStartDate,
    setEndDate,
    setPostThreshold,
    setAnalysisMode
  };
}; 
